/**
 * ImportPanel — вкладка «Импорт» админки СЗР: запуск импорта редакции
 * госкаталога прямо с экрана (AK-781; до этого здесь была заглушка «ETL в
 * разработке» с disabled-кнопкой).
 *
 * Главное правило экрана — **сначала план, потом применение**. Каталог СЗР
 * глобальный: он один на всю платформу, и ошибка импорта бьёт по всем
 * хозяйствам сразу. Поэтому «Применить» физически недоступно, пока оператор не
 * увидел цифры пробного прогона: «создам/обновлю N препаратов, M регламентов,
 * K в очередь на разбор, E ошибок». Это UI-половина SAFETY-протокола
 * data-integrations (dry-run → показать цифры → live).
 *
 * Импорт идёт ФОНОМ и опрашивается: полный дамп (~19 МБ) применяется минутами,
 * «толкни и жди» без обратной связи — прямой удар по третьему принципу UX.
 *
 * Два источника: «свежая редакция с портала» (основной, дамп ищется по
 * паспорту набора) и «загрузить файлом» (запасной — когда портал недоступен).
 */
import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileUp,
  Loader2,
  PlayCircle,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { PesticideSyncStatusPanel } from '@/components/pesticides/PesticideSyncStatusPanel';
import { formatRelativeRu, formatRuDate } from '@/lib/dateFmt';
import {
  useLatestImportRun,
  useStartPesticideImport,
  type ImportErrorCode,
  type ImportMode,
  type ImportRun,
  type ImportSource,
} from '@/lib/pesticides/import';

/**
 * Разбор ошибки бэка: человеку — текст, экрану — КОД.
 *
 * Ветвиться по тексту сообщения нельзя: стоит переформулировать фразу на
 * бэкенде, и ветка тихо перестаёт срабатывать, причём тесты этого не увидят
 * (ровно это чинили в #967). `rawDetail.code` — структурный контракт.
 */
function errorInfo(e: unknown): { text: string; code: ImportErrorCode | null } {
  const err = e as { detail?: unknown; rawDetail?: { code?: unknown } };
  const rawCode = err?.rawDetail?.code;
  return {
    text:
      typeof err?.detail === 'string'
        ? err.detail
        : e instanceof Error
          ? e.message
          : 'Не удалось запустить импорт.',
    code: typeof rawCode === 'string' ? (rawCode as ImportErrorCode) : null,
  };
}

export function ImportPanel() {
  const [source, setSource] = useState<ImportSource>('portal');
  const [file, setFile] = useState<File | null>(null);
  const [edition, setEdition] = useState('');
  const [allowOlder, setAllowOlder] = useState(false);
  const [error, setError] = useState<{
    text: string;
    code: ImportErrorCode | null;
  } | null>(null);
  /** Прогон, который запустил ЭТОТ экран: id + режим + слепок настроек. */
  const [launched, setLaunched] = useState<{
    id: string;
    mode: ImportMode;
    key: string;
  } | null>(null);
  /** Настройки, для которых уже показан и принят план (гейт «Применить»). */
  const [approvedKey, setApprovedKey] = useState<string | null>(null);

  const { data: run } = useLatestImportRun();
  const start = useStartPesticideImport();

  // Слепок настроек: сменил источник / файл / дату — план протух, гейт закрылся.
  // В ключ входит и `lastModified`: пересохранённый файл с тем же именем и
  // размером — это ДРУГАЯ редакция, и старый план к ней не относится.
  const currentKey = `${source}|${file?.name ?? ''}|${file?.size ?? ''}|${file?.lastModified ?? ''}|${edition}`;

  useEffect(() => {
    if (!run || !launched || run.id !== launched.id) return;
    // Режим сверяем по СВОЕМУ запуску, а не только по ответу: иначе применение
    // могло бы «переоткрыть» гейт, дождавшись ответа о прошлом пробном прогоне.
    if (
      launched.mode === 'dry_run' &&
      run.mode === 'dry_run' &&
      run.status === 'success'
    ) {
      setApprovedKey(launched.key);
    }
  }, [run, launched]);

  const running = run?.status === 'running';
  const canApply = approvedKey === currentKey && !running && !start.isPending;
  const submitting = start.isPending || running;

  async function submit(mode: ImportMode) {
    setError(null);
    if (source === 'file' && !file) {
      setError({ text: 'Выберите файл редакции.', code: 'file_missing' });
      return;
    }
    try {
      const started = await start.mutateAsync({
        mode,
        source,
        file,
        edition: edition || null,
        allowOlder,
      });
      setLaunched({ id: started.id, mode, key: currentKey });
      if (mode === 'live') {
        // План израсходован: следующее применение — снова после пробного прогона.
        setApprovedKey(null);
        setAllowOlder(false);
      }
    } catch (e) {
      setError(errorInfo(e));
    }
  }

  function pickFile(next: File | null) {
    setFile(next);
    setApprovedKey(null);
  }

  return (
    <div className="space-y-4">
      {/* Свежесть каталога: применённая редакция + «вышла новая» (is_stale). */}
      <PesticideSyncStatusPanel />

      <Card className="p-4">
        <h3 className="text-sm font-medium text-foreground">
          Импорт редакции госкаталога
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Сначала пробный прогон — он ничего не меняет и показывает, что именно
          будет загружено. Применить можно только после него.
        </p>

        <div className="mt-4">
          <Label className="mb-2 block text-xs">Источник редакции</Label>
          <RadioGroup
            value={source}
            onValueChange={(v) => {
              setSource(v as ImportSource);
              setApprovedKey(null);
              setError(null);
            }}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value="portal"
                id="src-portal"
                disabled={submitting}
              />
              <Label htmlFor="src-portal" className="text-sm font-normal">
                Взять свежую редакцию с портала Минсельхоза
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value="file"
                id="src-file"
                disabled={submitting}
              />
              <Label htmlFor="src-file" className="text-sm font-normal">
                Загрузить файлом (XML или ZIP) — если портал недоступен
              </Label>
            </div>
          </RadioGroup>
        </div>

        {source === 'file' ? (
          <div className="mt-4">
            <Label htmlFor="edition-file" className="mb-2 block text-xs">
              Файл редакции
            </Label>
            <Input
              id="edition-file"
              type="file"
              accept=".xml,.zip"
              disabled={submitting}
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <p className="mt-1 text-xs text-muted-foreground">
                <FileUp className="mr-1 inline h-3 w-3" aria-hidden />
                {file.name}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="mt-4 max-w-xs">
          <Label htmlFor="edition-date" className="mb-2 block text-xs">
            Дата редакции (необязательно)
          </Label>
          <Input
            id="edition-date"
            type="date"
            value={edition}
            disabled={submitting}
            onChange={(e) => {
              setEdition(e.target.value);
              setApprovedKey(null);
            }}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Обычно определяется автоматически. Заполните, если система не смогла
            определить дату редакции.
          </p>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            disabled={submitting}
            onClick={() => void submit('dry_run')}
            data-testid="btn-dry-run"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <PlayCircle className="mr-2 h-4 w-4" aria-hidden />
            )}
            Пробный прогон
          </Button>

          <Button
            disabled={!canApply}
            onClick={() => void submit('live')}
            data-testid="btn-apply"
            title={
              canApply
                ? undefined
                : 'Сначала пробный прогон — применить можно только после того, как увидели цифры'
            }
          >
            Применить
          </Button>

          {!canApply && !running ? (
            <span className="text-xs text-muted-foreground">
              Применение откроется после пробного прогона.
            </span>
          ) : null}
        </div>

        {error ? (
          <Alert variant="destructive" className="mt-4" data-testid="import-error">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            <AlertTitle>Импорт не запущен</AlertTitle>
            <AlertDescription>{error.text}</AlertDescription>
          </Alert>
        ) : null}

        {error?.code === 'edition_older' ? (
          <label className="mt-3 flex items-start gap-2 text-sm text-foreground">
            <Checkbox
              checked={allowOlder}
              onCheckedChange={(v) => setAllowOlder(v === true)}
              data-testid="allow-older"
            />
            <span>
              Я понимаю, что откатываю каталог к более старой редакции, и хочу
              это сделать.
            </span>
          </label>
        ) : null}
      </Card>

      {run ? <RunCard run={run} isOwn={launched?.id === run.id} /> : null}
    </div>
  );
}

/** Прогресс живого прогона + итоговые цифры завершившегося. */
function RunCard({ run, isOwn }: { run: ImportRun; isOwn: boolean }) {
  const isDry = run.mode === 'dry_run';
  const title = isDry ? 'Пробный прогон' : 'Применение редакции';

  // Чужой живой прогон (импорт запустил другой администратор — каталог один на
  // платформу) нельзя показывать как «твой»: человек ждал бы своих цифр,
  // которых не будет. Называем, кто и когда его начал.
  if (run.status === 'running' && !isOwn) {
    return (
      <Alert data-testid="run-foreign">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <AlertTitle>Импорт уже идёт</AlertTitle>
        <AlertDescription>
          Запустил {run.started_by_name ?? 'другой администратор'}
          {run.started_at ? `, ${formatRelativeRu(run.started_at)}` : ''}.
          {run.progress_total
            ? ` Обработано ${run.progress_current} из ${run.progress_total}.`
            : ''}{' '}
          Дождитесь завершения — одновременно идти может только один импорт.
        </AlertDescription>
      </Alert>
    );
  }

  if (run.status === 'running') {
    const total = run.progress_total ?? 0;
    const pct =
      total > 0
        ? Math.min(100, Math.round((run.progress_current / total) * 100))
        : null;
    return (
      <Card className="p-4" data-testid="run-progress">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
          <span className="text-sm font-medium text-foreground">
            {title}: идёт обработка
          </span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {total > 0
            ? `Обработано ${run.progress_current} из ${total} препаратов${pct !== null ? ` (${pct}%)` : ''}`
            : 'Загружается редакция…'}
        </p>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${pct ?? 5}%` }}
          />
        </div>
      </Card>
    );
  }

  if (run.status === 'failed') {
    return (
      <Alert variant="destructive" data-testid="run-failed">
        <AlertTriangle className="h-4 w-4" aria-hidden />
        <AlertTitle>{title} не удался</AlertTitle>
        <AlertDescription>
          {run.error ?? 'Причина неизвестна — посмотрите журнал сервера.'}
        </AlertDescription>
      </Alert>
    );
  }

  const r = run.report;
  return (
    <Card className="p-4" data-testid="run-report">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="text-sm font-medium text-foreground">
          {isDry ? 'План загрузки (ничего не изменено)' : 'Редакция применена'}
        </h3>
      </div>

      <p className="mt-1 text-sm text-muted-foreground">
        Редакция{' '}
        {run.source_edition ? formatRuDate(run.source_edition) : 'не определена'}
        {run.source === 'file' && run.source_name
          ? ` · файл ${run.source_name}`
          : ' · портал Минсельхоза'}
      </p>

      {r ? (
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Препаратов</dt>
            <dd className="text-foreground" data-testid="cnt-products">
              {isDry ? 'создам/обновлю ' : ''}
              {r.products_written} из {r.products_total}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Регламентов</dt>
            <dd className="text-foreground">{r.regulations_written}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">В очередь на разбор</dt>
            <dd className="text-foreground">{r.queued}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Ошибок</dt>
            <dd className="text-foreground">{r.errors}</dd>
          </div>
        </dl>
      ) : null}

      {r && r.error_samples.length > 0 ? (
        <details className="mt-3 text-xs text-muted-foreground">
          <summary className="cursor-pointer">
            Первые ошибки ({r.error_samples.length})
          </summary>
          <ul className="mt-1 space-y-1">
            {r.error_samples.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {isDry ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Проверьте цифры. Если всё верно — нажмите «Применить».
        </p>
      ) : null}
    </Card>
  );
}
